// src/shared/ask-secret.js
//
// v133.120 - a Shift pop-up for a password or a PIN (the 15 Oct 2026 look-and-feel review). Three places typed a
// secret into the browser's own prompt() box, which cannot hide what is typed: a manager's new password, a store's
// device password and the manager PIN for an add-on target. Anyone at the till or behind the screen could read it.
//
// askSecret(opts) -> Promise<string|null>   (null = cancelled; '' only when allowEmpty)
//   title      the question, shown as text (never HTML)
//   note       an optional line under it
//   kind       'password' (default) or 'pin' (numeric keypad on phones and tills)
//   minLength  refuse anything shorter (except an allowed empty answer)
//   allowEmpty an empty answer is a real answer (e.g. "remove the password")
//   okLabel    the confirm button's words (default "Save")
// The field is type=password with a "Show" toggle, the box is a real dialog (role, aria-modal, Escape cancels,
// Enter confirms, focus starts in the field), and nothing typed is kept once it closes.

export function askSecret(opts) {
  const o = opts || {};
  return new Promise(function (resolve) {
    if (typeof document === 'undefined') { resolve(null); return; }
    const pin = o.kind === 'pin';
    const back = document.createElement('div');
    back.setAttribute('style', 'position:fixed;inset:0;z-index:100000;background:rgba(17,24,20,.55);display:flex;align-items:center;justify-content:center;padding:16px');
    const box = document.createElement('div');
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', 'askSecretTitle');
    box.setAttribute('style', 'background:var(--card);color:var(--text);border-radius:14px;max-width:420px;width:100%;padding:20px;box-shadow:0 12px 40px rgba(0,0,0,.25);font-family:inherit');

    const h = document.createElement('div');
    h.id = 'askSecretTitle';
    h.setAttribute('style', 'font-size:17px;font-weight:700;margin-bottom:6px;white-space:pre-line');
    h.textContent = o.title || (pin ? 'Enter your PIN' : 'Enter a password');
    box.appendChild(h);
    if (o.note) {
      const n = document.createElement('div');
      n.setAttribute('style', 'font-size:14px;color:var(--sub);margin-bottom:12px;white-space:pre-line');
      n.textContent = o.note;
      box.appendChild(n);
    }

    const row = document.createElement('div');
    row.setAttribute('style', 'display:flex;gap:8px;align-items:center');
    const input = document.createElement('input');
    input.type = 'password';
    input.id = 'askSecretInput';
    input.setAttribute('autocomplete', pin ? 'off' : 'new-password');
    input.setAttribute('aria-labelledby', 'askSecretTitle');
    if (pin) { input.setAttribute('inputmode', 'numeric'); input.setAttribute('maxlength', '8'); }
    input.setAttribute('style', 'flex:1;min-width:0;font-size:16px;padding:11px 12px;border:1.5px solid var(--border-strong);border-radius:10px;' + (pin ? 'letter-spacing:6px;text-align:center;' : ''));
    const show = document.createElement('button');
    show.type = 'button';
    show.textContent = 'Show';
    show.setAttribute('style', 'min-height:44px;padding:0 14px;border:1.5px solid var(--border-strong);background:var(--card);border-radius:10px;font-size:14px;cursor:pointer');
    show.onclick = function () {
      const hidden = input.type === 'password';
      input.type = hidden ? 'text' : 'password';
      show.textContent = hidden ? 'Hide' : 'Show';
      input.focus();
    };
    row.appendChild(input);
    row.appendChild(show);
    box.appendChild(row);

    const err = document.createElement('div');
    err.setAttribute('role', 'alert');
    err.setAttribute('style', 'color:var(--adm-red-text);font-size:13px;min-height:18px;margin-top:6px');
    box.appendChild(err);

    const btns = document.createElement('div');
    btns.setAttribute('style', 'display:flex;gap:10px;justify-content:flex-end;margin-top:10px');
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.textContent = 'Cancel';
    cancel.setAttribute('style', 'min-height:44px;padding:0 18px;border:1.5px solid var(--border-strong);background:var(--card);border-radius:10px;font-size:15px;cursor:pointer');
    const ok = document.createElement('button');
    ok.type = 'button';
    ok.textContent = o.okLabel || 'Save';
    ok.setAttribute('style', 'min-height:44px;padding:0 20px;border:none;background:#007A2F;color:#fff;border-radius:10px;font-size:15px;font-weight:700;cursor:pointer');
    btns.appendChild(cancel);
    btns.appendChild(ok);
    box.appendChild(btns);
    back.appendChild(box);

    const before = document.activeElement;
    function close(value) {
      document.removeEventListener('keydown', onKey, true);
      input.value = '';
      if (back.parentNode) back.parentNode.removeChild(back);
      try { if (before && before.focus) before.focus(); } catch (e) { /* the opener may be gone */ }
      resolve(value);
    }
    function submit() {
      const v = input.value;
      if (!v && !o.allowEmpty) { err.textContent = pin ? 'Enter the PIN' : 'Enter a password'; input.focus(); return; }
      if (pin && v && !/^\d+$/.test(v)) { err.textContent = 'Numbers only'; input.focus(); return; }
      if (v && o.minLength && v.length < o.minLength) { err.textContent = 'At least ' + o.minLength + ' characters'; input.focus(); return; }
      close(v);
    }
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(null); }
      else if (e.key === 'Enter') { e.preventDefault(); submit(); }
      else if (e.key === 'Tab') {   // keep focus inside the box
        const f = [input, show, cancel, ok];
        const i = f.indexOf(document.activeElement);
        if (e.shiftKey && i <= 0) { e.preventDefault(); ok.focus(); }
        else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); input.focus(); }
      }
    }
    cancel.onclick = function () { close(null); };
    ok.onclick = submit;
    back.addEventListener('mousedown', function (e) { if (e.target === back) close(null); });
    document.addEventListener('keydown', onKey, true);
    document.body.appendChild(back);
    setTimeout(function () { input.focus(); }, 30);
  });
}
